import React, { FC } from 'react';
import { CampaignsCreditPackageCard } from './CampaignsCreditPackageCard';

export type CreditPackage = {
  id: string;
  title: string;
  messages: string;
  price: string;
  features: string[];
  ctaLabel: string;
  rateLabel?: string;
  isPopular?: boolean;
};

export type CreditPackagesBalance = {
  title: string;
  subtitle: string;
  description: string;
  availableBalanceLabel: string;
  availableBalanceValue: string;
  availableBalanceUnit: string;
  renewalHint: string;
};

export type CreditPackagesContent = {
  eyebrow: string;
  heading: string;
  subheading: string;
  balance: CreditPackagesBalance;
  packsEyebrow: string;
  packsHeading: string;
  volumeLabel: string;
  showPopularBadge?: boolean;
  solidPopularCta?: boolean;
  packages: CreditPackage[];
};

export const WHATSAPP_CREDIT_PACKAGES_CONTENT: CreditPackagesContent = {
  eyebrow: 'WhatsApp billing',
  heading: 'Top up your WhatsApp credit',
  subheading: 'Buy message bundles in advance and get better rates. Credit never expires and works across all your campaigns.',
  balance: {
    title: 'Pay-as-you-grow',
    subtitle: 'WhatsApp messaging credit',
    description:
      'No monthly fee, no surprises. One credit pool for marketing, utility and authentication templates. Billed only when a conversation actually delivers.',
    availableBalanceLabel: 'Credit Balance',
    availableBalanceValue: '45,000',
    availableBalanceUnit: 'credits available',
    renewalHint: 'Auto top-up enabled',
  },
  packsEyebrow: 'Choose your pack',
  packsHeading: 'The more you buy, the less you pay',
  volumeLabel: 'messages',
  showPopularBadge: true,
  solidPopularCta: true,
  packages: [
    {
      id: 'starter',
      title: 'Starter',
      messages: '5k',
      price: '€50',
      features: ['1,000 conversations', 'No setup cost', 'Standard delivery rate'],
      ctaLabel: 'Buy starter',
    },
    {
      id: 'growth',
      title: 'Growth',
      messages: '25k',
      price: '€225',
      features: ['5,000 conversations', 'Volume discount 10%', 'Priority support'],
      ctaLabel: 'Buy growth',
      isPopular: true,
    },
    {
      id: 'business',
      title: 'Business',
      messages: '100k',
      price: '€800',
      features: ['20,000 conversations', '24/7 priority support', 'API billing unit'],
      ctaLabel: 'Buy business',
    },
    {
      id: 'enterprise',
      title: 'Enterprise',
      messages: '500k',
      price: '€3500',
      features: ['100,000 conversations', 'Custom SLA and DPA', 'API plus webhook limits'],
      ctaLabel: 'Buy enterprise',
    },
  ],
};

type CampaignsCreditPackagesSectionProps = {
  content?: CreditPackagesContent;
};

export const CampaignsCreditPackagesSection: FC<CampaignsCreditPackagesSectionProps> = ({ content = WHATSAPP_CREDIT_PACKAGES_CONTENT }) => {
  const { balance } = content;

  return (
    <section className='space-y-6 rounded-3xl border border-secondary-border bg-surface p-5 shadow-sm md:p-6'>
      <div className='space-y-1'>
        <p className='text-xs font-semibold uppercase tracking-[0.16em] text-quaternary-500'>{content.eyebrow}</p>
        <h2 className='text-3xl font-semibold text-slate-900'>{content.heading}</h2>
        <p className='text-sm text-secondary-600'>{content.subheading}</p>
      </div>

      <div className='rounded-2xl bg-gradient-to-r from-blue-800 to-blue-600 p-5 text-white-100 md:p-6'>
        <div className='flex flex-col gap-5 md:flex-row md:items-end md:justify-between'>
          <div className='max-w-2xl space-y-2'>
            <p className='text-xs font-semibold uppercase tracking-[0.16em] text-blue-100'>{balance.title}</p>
            <h3 className='text-2xl font-semibold'>{balance.subtitle}</h3>
            <p className='text-sm leading-6 text-blue-100'>{balance.description}</p>
          </div>

          <div className='w-full rounded-xl border border-blue-300/40 bg-blue-900/30 p-4 md:w-auto md:min-w-[220px]'>
            <p className='text-[11px] uppercase tracking-[0.16em] text-blue-100'>{balance.availableBalanceLabel}</p>
            <p className='mt-2 text-3xl font-semibold'>{balance.availableBalanceValue}</p>
            <p className='text-xs text-blue-100'>{balance.availableBalanceUnit}</p>
            <p className='mt-3 text-xs text-blue-100'>{balance.renewalHint}</p>
          </div>
        </div>
      </div>

      <div className='space-y-4'>
        <div className='text-center'>
          <p className='text-xs font-semibold uppercase tracking-[0.16em] text-quaternary-500'>{content.packsEyebrow}</p>
          <h3 className='text-xl font-semibold text-slate-900'>{content.packsHeading}</h3>
        </div>

        <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-4'>
          {content.packages.map((item) => (
            <CampaignsCreditPackageCard
              key={item.id}
              title={item.title}
              messages={item.messages}
              price={item.price}
              features={item.features}
              ctaLabel={item.ctaLabel}
              rateLabel={item.rateLabel}
              isPopular={item.isPopular}
              volumeLabel={content.volumeLabel}
              showPopularBadge={content.showPopularBadge}
              solidPopularCta={content.solidPopularCta}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
